/**
 * The UI for keeping the coach and the bay when a booking is moved (DNKADM-0488): the kept
 * coach/bay with Change controls (above the time picker) and the "Any free bay" check result
 * (below it). The state lives in `useKeepAssignment` (`./keepAssignment`).
 */
import type { Ref } from 'react';
import { Alert, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { scrubBrand } from '@/utils/whitelabel';
import { ANY_BAY, type KeepAssignment, type KeptOption } from './keepAssignment';

function optionName(option: KeptOption, fallback: string): string {
  return scrubBrand(option.name ?? '') || fallback;
}

interface KeptFieldsProps {
  keep: KeepAssignment;
  disabled?: boolean;
  coachFieldRef?: Ref<HTMLDivElement>;
  bayFieldRef?: Ref<HTMLDivElement>;
  /** The coach option's label (Edit time marks a refused pick "(conflict)"). */
  coachLabel?: (option: KeptOption) => string;
}

/** The kept coach and bay, each with its Change control — rendered above the time picker. */
export function KeptAssignmentFields({
  keep,
  disabled = false,
  coachFieldRef,
  bayFieldRef,
  coachLabel,
}: KeptFieldsProps) {
  const { coach, bay } = keep;
  if (!coach.active && !bay.active) return null;
  const labelCoach = coachLabel ?? ((o: KeptOption) => optionName(o, 'Unnamed staff'));
  return (
    <Stack sx={{ gap: 1, mb: 1.5 }} data-testid="kept-assignment">
      {coach.active && coach.current && (
        coach.changing ? (
          <TextField
            ref={coachFieldRef}
            select
            label="Coach"
            size="small"
            value={coach.choice}
            onChange={(e) => coach.setChoice(e.target.value)}
            disabled={disabled || coach.poolStatus === 'loading'}
            helperText={
              coach.poolStatus === 'error' ? 'The coach list couldn’t be loaded.' : ' '
            }
          >
            {coach.options.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {labelCoach(o)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              Coach: <strong>{labelCoach(coach.current)}</strong>
            </Typography>
            <Button size="small" onClick={coach.startChange} disabled={disabled}>
              Change coach
            </Button>
          </Stack>
        )
      )}
      {bay.active && bay.current && (
        bay.changing ? (
          <TextField
            ref={bayFieldRef}
            select
            label="Bay"
            size="small"
            value={bay.choice}
            onChange={(e) => bay.setChoice(e.target.value)}
            disabled={disabled || bay.poolStatus === 'loading'}
            helperText={bay.poolStatus === 'error' ? 'The bay list couldn’t be loaded.' : ' '}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            {bay.options.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {optionName(o, 'Unnamed bay')}
              </MenuItem>
            ))}
            {/* Only with the whole pool loaded can every bay be checked at a picked time. */}
            {bay.poolStatus === 'ready' && <MenuItem value={ANY_BAY}>Any free bay</MenuItem>}
          </TextField>
        ) : (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              Bay: <strong>{optionName(bay.current, 'Unnamed bay')}</strong>
            </Typography>
            <Button size="small" onClick={bay.startChange} disabled={disabled}>
              Change bay
            </Button>
          </Stack>
        )
      )}
    </Stack>
  );
}

/**
 * Under "Any free bay", what the per-time check found — rendered below the time picker. Nothing
 * when the current bay is free (it is kept) or when no time is picked.
 */
export function BayCheckNotice({ keep, disabled = false }: { keep: KeepAssignment; disabled?: boolean }) {
  const { bay } = keep;
  const check = bay.check;
  if (!bay.active || bay.current === null || check === null) return null;
  if (check.status === 'loading') {
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mt: 1 }} role="status">
        <CircularProgress size={16} />
        <Typography variant="body2">Checking which bays are free at this time…</Typography>
      </Stack>
    );
  }
  if (check.status === 'error') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Couldn’t check which bays are free at this time. Pick the time again, or pick a bay above.
      </Alert>
    );
  }
  if (check.free.some((b) => b.id === bay.current?.id)) return null;
  const currentName = optionName(bay.current, 'The current bay');
  if (check.free.length === 0) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }} role="alert">
        {currentName} is taken at this time, and no other bay is free. Pick another time.
      </Alert>
    );
  }
  return (
    <Alert severity="warning" sx={{ mt: 1 }} role="alert">
      <Typography variant="body2" sx={{ mb: 1 }}>
        {currentName} is taken at this time. Pick a free bay:
      </Typography>
      <TextField
        select
        label="Free bay"
        size="small"
        required
        value={bay.takenPick}
        onChange={(e) => bay.setTakenPick(e.target.value)}
        disabled={disabled}
        sx={{ minWidth: 200 }}
      >
        {check.free.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {optionName(o, 'Unnamed bay')}
          </MenuItem>
        ))}
      </TextField>
    </Alert>
  );
}
